import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

function RevealLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const chips = [
  { label: "SENTIMENT", value: "+87%", pos: "top-6 -left-4 lg:-left-10", delay: 1.6, color: "text-[#4CD964]" },
  { label: "FOOTFALL MOOD", value: "LIVE", pos: "top-1/3 -right-4 lg:-right-12", delay: 1.8, color: "text-[#E5FF00]" },
  { label: "URGENCY FLAGS", value: "02", pos: "bottom-8 -left-4 lg:-left-14", delay: 2.0, color: "text-[#FF2D55]" },
];

export default function Hero({ onGetStarted }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const frameY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen grid-lines overflow-hidden" data-testid="hero-section">
      <div className="spotlight absolute inset-0 pointer-events-none" />
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 pt-36 lg:pt-44 pb-24 grid lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-7 relative z-10">
          <motion.p
            className="font-mono-tech text-[11px] tracking-[0.35em] text-[#E5FF00] mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            data-testid="hero-kicker"
          >
            LYPINN PULSE — REALTIME AUDIENCE INTELLIGENCE
          </motion.p>
          <h1 className="font-display font-black leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-8xl" data-testid="hero-headline">
            <RevealLine delay={0.4}>YOUR AUDIENCE</RevealLine>
            <RevealLine delay={0.55}>IS TALKING.</RevealLine>
            <RevealLine delay={0.7}>
              <span className="text-[#E5FF00]">START LISTENING.</span>
            </RevealLine>
          </h1>
          <motion.p
            className="mt-8 max-w-xl text-base md:text-lg text-zinc-400 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8, ease: EASE }}
            data-testid="hero-subcopy"
          >
            Realtime, credible sentiment from the people physically inside your mall, event, park, station or campus.
            Captured through PulsePoint print media, PulseScreen kiosks and the LyPinn app. No guesswork — pure data points.
          </motion.p>
          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8, ease: EASE }}
          >
            <button
              onClick={onGetStarted}
              className="group flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold text-sm tracking-wide px-8 py-4 hover:bg-white transition-colors duration-200"
              data-testid="hero-cta-start"
            >
              START CAPTURING SENTIMENT
              <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.div>
          <motion.div
            className="mt-14 flex gap-10 font-mono-tech text-[11px] tracking-[0.2em] text-zinc-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
          >
            <span data-testid="hero-stat-latency"><span className="text-white">&lt;5s</span> SIGNAL LATENCY</span>
            <span data-testid="hero-stat-firstparty"><span className="text-white">100%</span> FIRST-PARTY</span>
            <span data-testid="hero-stat-guesswork"><span className="text-[#E5FF00]">0</span> GUESSWORK</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            style={{ y: frameY }}
            className="relative"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.9, duration: 1, ease: EASE }}
          >
            <div className="relative overflow-hidden border border-white/15" style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 88% 100%, 0 100%)" }}>
              <motion.img
                style={{ y: imgY }}
                src="https://images.unsplash.com/photo-1701852965474-35ad0d54a0b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtYWxsJTIwY3Jvd2R8ZW58MHx8fHwxNzg3NTk0NjU3fDA&ixlib=rb-4.1.0&q=85"
                alt="Audience inside a modern venue"
                className="w-full h-[560px] object-cover scale-110"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              <div className="scanline absolute left-0 right-0 h-px bg-[#E5FF00]/60 shadow-[0_0_18px_rgba(229,255,0,0.7)]" />
              <div className="absolute bottom-4 left-4 font-mono-tech text-[10px] tracking-[0.25em] text-zinc-400">
                ZONE A — CONCOURSE · LIVE FEED
              </div>
            </div>
            {chips.map((c) => (
              <motion.div
                key={c.label}
                className={`absolute ${c.pos} bg-black/70 backdrop-blur-xl border border-white/15 px-4 py-3`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: c.delay, duration: 0.7, ease: EASE }}
                data-testid={`hero-chip-${c.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <p className="font-mono-tech text-[9px] tracking-[0.25em] text-zinc-500">{c.label}</p>
                <p className={`font-mono-tech text-xl font-bold ${c.color}`}>{c.value}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
